import { ChartingFailureRelatedIDs, CHARTING_TUBE_LABELING_CHECK_ID, WaypointActionType, ProcedureStatus, WaypointActionStatusName } from '@caresend/types';
import { isNullish, hasStatus } from '@caresend/utils';
import { defineComponent, ref, computed, watch, onUnmounted } from 'vue';
import __vue_component__$1 from '../../../../../booking/ChartingItemGroup.vue.js';
import __vue_component__ from './ChartingHospitalConfirm.vue.js';
import __vue_component__$2 from './ChartingTubeConfirm.vue.js';
import { trackChartingDone } from '../../../../helpers/tracking.js';
import { usePortalStore } from '../../../../store/portal/index.js';
import { setProcedureChartingItems } from '../../../../store/portal/set/procedure.js';
import { UNLABELED_TUBES_MODAL_VARIANT } from '../../../../store/portal/workflow/items/procedures.js';
import __vue_component__$3 from '../../../../../../ui/Loading/Loading.vue.js';
import '@sentry/vue';
import { toastError } from '../../../../../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../../../../ui/Icon.vue.js';
import '../../../../../../../functions/configuration/icons.js';
import '../../../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../../../functions/tracking/index.js';
import { getChartingResultInputs } from '../../../../../../../functions/bookings.js';
import { getConditionalFollowUpDisplayMap, chartingComplete, buildProcedureFailedFromCharting } from '../../../../../../../functions/charting.js';

var script = defineComponent({
    name: 'ItineraryWaypointProcedureCharting',
    components: {
        ChartingHospitalConfirm: __vue_component__,
        ChartingItemGroup: __vue_component__$1,
        ChartingTubeConfirm: __vue_component__$2,
        Loading: __vue_component__$3,
    },
    model: {
        prop: 'value',
        event: 'update:value',
    },
    props: {
        procedureID: {
            type: String,
            required: true,
        },
        waypointActionID: {
            type: String,
            required: true,
        },
        value: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:value', 'setFailOnComplete', 'setModalMetaVariant'],
    setup(props, { emit }) {
        const { procedures, waypointActions } = usePortalStore();
        const isLoading = ref(false);
        const procedure = computed(() => procedures.get(props.procedureID));
        const chartingItems = computed(() => { var _a, _b, _c; return (_c = (_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.charting) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : []; });
        // If charting item id is in the map, then the item should be shown conditionally
        const conditionalFollowUpDisplayMap = computed(() => getConditionalFollowUpDisplayMap(chartingItems.value));
        const resultsChartingItems = computed(() => {
            if (!chartingItems.value)
                return null;
            return getChartingResultInputs(chartingItems.value);
        });
        const isChartingComplete = computed(() => {
            if (isNullish(chartingItems.value))
                return false;
            const isComplete = chartingComplete({
                chartingItems: chartingItems.value,
                procedureID: props.procedureID,
            });
            return isComplete;
        });
        const getPatientWaypointActionStatusToUpdateAndID = () => {
            var _a, _b, _c, _d;
            // Updated the waypoint action status to complete which is associated with the patient
            const patientWaypointAction = Object.values((_b = (_a = procedure.value) === null || _a === void 0 ? void 0 : _a.computed.waypointActions) !== null && _b !== void 0 ? _b : {}).find((waypointAction) => waypointAction.type === WaypointActionType.PATIENT_ACTION);
            if (!patientWaypointAction) {
                toastError('Patient waypoint action not found for this procedure');
                return;
            }
            // maybe update the waypoint action status to complete if all the procedures are completed
            // TODO: this is an interim solution, we should refactor to do this at the end of the workflow item
            let completeProcedures = 0;
            let canceledProcedures = 0;
            Object.values((_d = (_c = waypointActions.get(patientWaypointAction.id)) === null || _c === void 0 ? void 0 : _c.computed.procedures) !== null && _d !== void 0 ? _d : {}).forEach((proc) => {
                if (hasStatus(proc, ProcedureStatus.COMPLETED))
                    completeProcedures += 1;
                if (hasStatus(proc, ProcedureStatus.CANCELED))
                    canceledProcedures += 1;
            });
            const proceduresCount = completeProcedures + canceledProcedures;
            let wpaStatus = WaypointActionStatusName.IN_PROGRESS;
            if (canceledProcedures > 0)
                wpaStatus = WaypointActionStatusName.CANCELED;
            if (completeProcedures === proceduresCount)
                wpaStatus = WaypointActionStatusName.COMPLETE;
            return [patientWaypointAction, wpaStatus];
        };
        const trackChartingItemsAreDone = () => {
            // TODO: Get back to this and refactor - need to set status as part of the workflow item
            // // Update the waypoint action status for the patientWaypointAction associated with this procedure
            // const [patientWaypointAction, wpaStatus] = getPatientWaypointActionStatusToUpdateAndID() ?? [];
            // if (patientWaypointAction && wpaStatus && wpaStatus !== WaypointActionStatusName.IN_PROGRESS) {
            //   waypointActions.setChildOf(
            //     patientWaypointAction.id,
            //     'status',
            //     {
            //       status: wpaStatus,
            //       timestamp: Date.now(),
            //     },
            //     `Updated waypoint action status to ${wpaStatus} for patient waypoint action with id ${
            //       patientWaypointAction.id
            //     }`,
            //   );
            // }
            var _a;
            // track results
            (_a = resultsChartingItems.value) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
                var _a;
                trackChartingDone(props.waypointActionID, props.procedureID, (_a = item.productID) !== null && _a !== void 0 ? _a : '', item.selection);
            });
        };
        const failureReasonItems = computed(() => {
            var _a, _b;
            if (!chartingItems.value)
                return [];
            return (_b = (_a = chartingItems.value.find((item) => item.id === ChartingFailureRelatedIDs.CHARTING_FAILURE_REASON)) === null || _a === void 0 ? void 0 : _a.children) !== null && _b !== void 0 ? _b : [];
        });
        // It's usually the first item in the charting items array, but just in case, look for it
        const shouldFail = computed(() => {
            var _a, _b;
            return ((_b = (_a = chartingItems.value) === null || _a === void 0 ? void 0 : _a.find((item) => item.id === ChartingFailureRelatedIDs.CHARTING_FAILURE)) === null || _b === void 0 ? void 0 : _b.selection) === 'No';
        });
        /**
         * The tube labeling item is a special case and it's currently 'optional' in the db since we
         * want to enforce it being checked by the user. If the user forgets to check on the checkbox and
         * go through the 'confirmation' modal, we allow the user to click on the charting modal's footer
         * button, but instead open a modal warning them that they need to go back and confirm the tubes
         * were labeled.
         *
         * The special modal is defined by the UNLABELED_TUBES_MODAL_VARIANT on the procedure workflow item
         * and is shown whenever the charting is complete but the tubes were not labeled.
         */
        const tubeLabelingItem = ref(undefined);
        const tubeLabelingItemIndex = ref(-1);
        const tubesLabeled = computed(() => { var _a, _b, _c; return (_c = (_b = (_a = tubeLabelingItem.value) === null || _a === void 0 ? void 0 : _a.checklist) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.checked; });
        const tubeLabelingModalIsOpen = ref(false);
        const handleLabelingConfirmed = (confirmed) => {
            if (!tubesLabeled.value || confirmed)
                return;
            // revert check if the user did not confirm the labeling
            setProcedureChartingItems({
                procedureID: props.procedureID,
                chartingItemIndexArray: [tubeLabelingItemIndex.value],
                newChartingChecklistItem: { checked: false },
                checklistItemIndex: 0,
            });
        };
        const hospitalConfirmationParentItem = ref(undefined);
        const hospitalConfirmationParentItemIndex = ref(-1);
        const isHospitalCollection = computed(() => { var _a, _b, _c, _d, _e; return (_e = (_d = (_c = (_b = (_a = hospitalConfirmationParentItem.value) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.checklist) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.checked; });
        const hospitalConfirmationModalIsOpen = ref(false);
        const handleHospitalConfirmed = (confirmed) => {
            if (!isHospitalCollection.value || confirmed)
                return;
            // revert check if the user did not confirm the hospital
            setProcedureChartingItems({
                procedureID: props.procedureID,
                chartingItemIndexArray: [hospitalConfirmationParentItemIndex.value, 0],
                newChartingChecklistItem: { checked: false },
                checklistItemIndex: 0,
            });
        };
        // If the procedure should fail and the charting is complete, build the procedure failed object
        watch([failureReasonItems, shouldFail, isChartingComplete], ([failureItems, fail, complete]) => {
            if (!complete)
                return;
            const procedureFailedObj = fail ? buildProcedureFailedFromCharting(failureItems) : undefined;
            procedures.setChildOf(props.procedureID, 'failed', procedureFailedObj, `Updated procedure ${props.procedureID} failed object`);
        });
        watch(shouldFail, (failOnComplete) => emit('setFailOnComplete', failOnComplete), { immediate: true });
        // Find the tube labeling item and index in the charting items array
        watch(chartingItems, (items) => {
            if (!items || items.length === 0)
                return;
            tubeLabelingItemIndex.value = items.findIndex((item) => item.id === CHARTING_TUBE_LABELING_CHECK_ID);
            hospitalConfirmationParentItemIndex.value = items.findIndex((item) => { var _a, _b, _c, _d; return item.type === 'group' && ((_d = (_c = (_b = (_a = item.children) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.checklist) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.label) === 'Hospital'; });
            if (tubeLabelingItemIndex.value !== -1) {
                tubeLabelingItem.value = items[tubeLabelingItemIndex.value];
            }
            if (hospitalConfirmationParentItemIndex.value !== -1) {
                hospitalConfirmationParentItem.value = items[hospitalConfirmationParentItemIndex.value];
            }
        }, { immediate: true });
        watch([tubesLabeled, isChartingComplete], ([labeled, complete], [prevLabeled, prevComplete]) => {
            if (prevLabeled === false && labeled)
                tubeLabelingModalIsOpen.value = true;
            const isUnlabeledTubesVariant = complete && !labeled;
            const wasUnlabeledTubesVariant = prevComplete && !prevLabeled;
            if (isUnlabeledTubesVariant && !wasUnlabeledTubesVariant) {
                emit('setModalMetaVariant', UNLABELED_TUBES_MODAL_VARIANT);
            }
            else if (wasUnlabeledTubesVariant && !isUnlabeledTubesVariant) {
                emit('setModalMetaVariant', undefined);
            }
        }, { immediate: true });
        watch(isHospitalCollection, (hospitalCollection, prevHospitalCollection) => {
            if (prevHospitalCollection === false && hospitalCollection) {
                hospitalConfirmationModalIsOpen.value = true;
            }
        }, { immediate: true });
        watch(isChartingComplete, (next, prev) => {
            if (next !== prev) {
                emit('update:value', !next);
                if (next)
                    trackChartingItemsAreDone();
            }
        }, { immediate: true });
        onUnmounted(() => { emit('setModalMetaVariant', undefined); });
        return {
            chartingItems,
            conditionalFollowUpDisplayMap,
            getPatientWaypointActionStatusToUpdateAndID,
            handleLabelingConfirmed,
            handleHospitalConfirmed,
            hospitalConfirmationModalIsOpen,
            isLoading,
            tubeLabelingModalIsOpen,
            tubesLabeled,
        };
    },
});

export { script as default };
